import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { InvitePreview, acceptInvite, getInvite, pathForItem } from '../../api/sharingApi';
import { fill } from './fill';
import './sharing.css';

// The page an invite link lands on. It works signed out — the preview needs
// no account — because the whole point of the link is that the person may
// not have one yet.
const InvitePage: React.FC = () => {
  const { t } = useLanguage();
  const { token = '' } = useParams<{ token: string }>();
  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const isAuthenticated = !!localStorage.getItem('token');

  useEffect(() => {
    let cancelled = false;
    getInvite(token)
      .then((data) => { if (!cancelled) setInvite(data); })
      .catch(() => { if (!cancelled) setError(t.sharing.inviteInvalid); });
    return () => { cancelled = true; };
  }, [token, t]);

  const accept = async () => {
    setAccepting(true);
    setError('');
    try {
      const target = await acceptInvite(token);
      // Full navigation: the app reads which view to open from the URL when
      // it boots, and this lands the person inside the shared thing itself.
      window.location.assign(pathForItem(target.kind, target.id));
    } catch (err: any) {
      const status = err.response?.status;
      setError(status === 410 ? t.sharing.inviteUsed
        : status === 404 ? t.sharing.inviteInvalid
          : t.sharing.inviteFailed);
      setAccepting(false);
    }
  };

  const returnTo = `/invite/${token}`;

  return (
    <div className="invite-page">
      <div className="invite-card">
        <img src="/logo192.png" alt="Yarnboard" className="invite-logo" />

        {invite ? (
          <>
            <h1 className="invite-headline">
              {fill(invite.role === 'editor' ? t.sharing.invitedYouEdit : t.sharing.invitedYouView,
                { name: invite.invited_by, title: invite.name })}
            </h1>
            {error && <div className="share-error">{error}</div>}

            {isAuthenticated ? (
              <button type="button" className="invite-accept" disabled={accepting} onClick={accept}>
                {accepting ? t.sharing.inviteAccepting : t.sharing.inviteAccept}
              </button>
            ) : (
              <>
                <p className="invite-hint">
                  {fill(t.sharing.inviteSignedOutHint, { email: invite.email })}
                </p>
                <div className="invite-actions">
                  <a className="invite-accept" href={`/login?next=${encodeURIComponent(returnTo)}`}>
                    {t.sharing.inviteSignIn}
                  </a>
                  <a className="invite-secondary" href={`/register?next=${encodeURIComponent(returnTo)}`}>
                    {t.sharing.inviteCreateAccount}
                  </a>
                </div>
              </>
            )}
          </>
        ) : (
          <p className="invite-status">{error || t.common.loading}</p>
        )}
      </div>
    </div>
  );
};

export default InvitePage;
